import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import ComingSoonOverlay from "../../components/Common/ComingSoonOverlay"
import ListingTabs from "../../components/Listing/ListingTabs"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { setUser } from "../../store/slices/authSlice"
import { CityHubAside, CityHubBanner, CityHubGenome } from "./CityHubPanels"
import {
  VISIBLE_CITY_HUB_TABS,
  cityHubTabPath,
  getCityHubTabByPath,
} from "./cityHubTabs"
import {
  getUserCityHubLevel,
  hasFullCityHubAccess,
  mapApiCityToCityHubCity,
  mapUserAddressesToExploreCities,
  pickDefaultCityHubCity,
  resolveCityHubCityForLevel,
} from "./cityHubCity"

const DEFAULT_TAB_PATH = "calls-for-artists"

const tabPathFromLocation = pathname => {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean)
  if (parts[0] !== "city-hub") return DEFAULT_TAB_PATH
  if (parts[2]) return parts[2]
  if (parts[1] && getCityHubTabByPath(parts[1])) return parts[1]
  return DEFAULT_TAB_PATH
}

export const CityHubCityRedirect = () => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const city = pickDefaultCityHubCity(currentUser)
  if (city?.id) {
    return <Navigate to={cityHubTabPath(DEFAULT_TAB_PATH, city.id)} replace />
  }
  return <Navigate to={`/city-hub/${DEFAULT_TAB_PATH}`} replace />
}

export const CityHubTabIndexRedirect = () => {
  const { cityId } = useParams()
  const currentUser = useSelector(state => state.auth.currentUser)
  const legacyTab = getCityHubTabByPath(cityId)
  if (legacyTab) {
    const city = pickDefaultCityHubCity(currentUser)
    if (city?.id) {
      return <Navigate to={cityHubTabPath(legacyTab, city.id)} replace />
    }
    return null
  }
  return <Navigate to={DEFAULT_TAB_PATH} replace />
}

/**
 * CityHubLayout
 * ------------------------------------------------------------------
 * Shared City Hub shell (banner, genome, tabs, aside).
 * Loads the current user's cities and keeps the selected banner city
 * in sync with `/city-hub/:cityId/:tab`.
 */
const cityFromLocationState = (locationState, cityIdParam) => {
  const fromState = locationState?.city
  if (
    fromState?.id &&
    cityIdParam &&
    String(fromState.id) === String(cityIdParam)
  ) {
    return fromState
  }
  return null
}

const CityHubLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { cityId: cityIdParam } = useParams()
  const currentUser = useSelector(state => state.auth.currentUser)
  const [isCityLoading, setIsCityLoading] = useState(true)
  const [selectedCity, setSelectedCity] = useState(() =>
    cityFromLocationState(location.state, cityIdParam),
  )
  const [recentPhotosRevision, setRecentPhotosRevision] = useState(0)

  const bumpRecentPhotos = useCallback(() => {
    setRecentPhotosRevision(prev => prev + 1)
  }, [])

  const tabPath = tabPathFromLocation(location.pathname)
  const activeTab = getCityHubTabByPath(tabPath)
  const isLegacyTabParam = Boolean(getCityHubTabByPath(cityIdParam))

  const yourCities = useMemo(
    () => mapUserAddressesToExploreCities(currentUser),
    [currentUser],
  )

  const userLevel = useMemo(
    () => getUserCityHubLevel(currentUser),
    [currentUser],
  )

  useEffect(() => {
    const fromState = cityFromLocationState(location.state, cityIdParam)
    if (!fromState) return
    setSelectedCity(prev =>
      prev && String(prev.id) === String(fromState.id) ? prev : fromState,
    )
  }, [cityIdParam, location.state])

  // Visiting city (e.g. from public page) — hydrate name/banner when not owned.
  useEffect(() => {
    if (!cityIdParam || isLegacyTabParam) return undefined

    const ownedMatch = yourCities.find(
      item => String(item.id) === String(cityIdParam),
    )
    if (ownedMatch) return undefined

    const hasVisitChrome =
      selectedCity &&
      String(selectedCity.id) === String(cityIdParam) &&
      selectedCity.name &&
      selectedCity.name !== "Visiting" &&
      selectedCity.banner

    if (hasVisitChrome) return undefined

    let cancelled = false

    const hydrateVisitCity = async () => {
      try {
        const response = await get(endpoints.getPublicCity(cityIdParam), {
          skipErrorToast: true,
        })
        if (cancelled) return
        if (!response?.data?.success || !response.data.result) return

        const mapped = mapApiCityToCityHubCity(response.data.result, {
          typeLabel: "Visiting",
        })
        if (!mapped?.id) return

        setSelectedCity({
          ...mapped,
          isOwned: false,
          isVisitOnly: true,
        })
      } catch {
        // Keep placeholder until Explore Cities / owned list loads.
      }
    }

    hydrateVisitCity()
    return () => {
      cancelled = true
    }
    // selectedCity intentionally omitted — re-run on city id / owned list only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityIdParam, isLegacyTabParam, yourCities])

  const city = useMemo(() => {
    const defaultCity = pickDefaultCityHubCity(currentUser)
    if (cityIdParam && !isLegacyTabParam) {
      const ownedMatch = yourCities.find(
        item => String(item.id) === String(cityIdParam),
      )
      if (ownedMatch) {
        return resolveCityHubCityForLevel(ownedMatch, userLevel)
      }
      if (selectedCity && String(selectedCity.id) === String(cityIdParam)) {
        return resolveCityHubCityForLevel(selectedCity, userLevel)
      }
      // Never borrow primary-city banner chrome for a different visit id.
      return {
        id: cityIdParam,
        name: "Visiting",
        shortName: "Visiting",
        type: "Visiting",
        isOwned: false,
        isVisitOnly: true,
        banner: null,
        thumb: null,
        tagline: "Discover, connect, and create opportunities in your city.",
      }
    }
    if (selectedCity?.id) {
      return resolveCityHubCityForLevel(selectedCity, userLevel)
    }
    return defaultCity
  }, [
    cityIdParam,
    currentUser,
    isLegacyTabParam,
    selectedCity,
    userLevel,
    yourCities,
  ])

  const isCityMember = useMemo(
    () => hasFullCityHubAccess(city?.id, yourCities, userLevel),
    [city?.id, yourCities, userLevel],
  )

  useEffect(() => {
    let cancelled = false

    const loadUserCity = async () => {
      setIsCityLoading(true)
      try {
        const response = await get(endpoints.getMe, {
          params: { with: "addresses.city" },
        })
        if (!cancelled && response?.data?.success) {
          dispatch(setUser(response.data.result))
          setSelectedCity(prev => {
            if (prev?.id) return prev
            return pickDefaultCityHubCity(response.data.result)
          })
        }
      } finally {
        if (!cancelled) setIsCityLoading(false)
      }
    }

    loadUserCity()
    return () => {
      cancelled = true
    }
  }, [dispatch])

  useEffect(() => {
    if (!city?.id) return
    const tabRootPath = cityHubTabPath(tabPath || DEFAULT_TAB_PATH, city.id)
    if (
      location.pathname === tabRootPath ||
      location.pathname.startsWith(`${tabRootPath}/`)
    ) {
      return
    }
    navigate(tabRootPath, { replace: true })
  }, [city?.id, location.pathname, navigate, tabPath])

  const handleSelectCity = useCallback(
    nextCity => {
      if (!nextCity?.id) return
      const resolved = resolveCityHubCityForLevel(nextCity, userLevel)
      setSelectedCity(resolved)
      navigate(cityHubTabPath(tabPath || DEFAULT_TAB_PATH, resolved.id))
    },
    [navigate, tabPath, userLevel],
  )

  const tabItems = useMemo(
    () =>
      VISIBLE_CITY_HUB_TABS.map(tab => ({
        key: tab.path,
        label: tab.label,
        icon: tab.icon,
      })),
    [],
  )

  if (!activeTab) {
    return (
      <Navigate
        to={cityHubTabPath(DEFAULT_TAB_PATH, city?.id || cityIdParam)}
        replace
      />
    )
  }

  const outletContext = {
    tab: activeTab,
    location,
    city,
    yourCities,
    isCityMember,
    isCityLoading,
    onSelectCity: handleSelectCity,
    refreshRecentPhotos: bumpRecentPhotos,
  }

  return (
    <div className="page-content">
      <div
        className={`city-hub${
          activeTab.key === "calls-for-artists" ? " city-hub--calls" : ""
        }`}
      >
        <CityHubBanner
          city={city}
          yourCities={yourCities}
          isLoading={isCityLoading}
          onSelectCity={handleSelectCity}
        />
        <CityHubGenome city={city} />

        <div className="city-hub__main">
          <div className="city-hub__tabs-row">
            <ListingTabs
              items={tabItems}
              activeKey={activeTab.path}
              onChange={nextPath => {
                const nextTab = getCityHubTabByPath(nextPath)
                if (nextTab) navigate(cityHubTabPath(nextTab, city?.id))
              }}
            />
          </div>

          {activeTab.comingSoon ? (
            <ComingSoonOverlay>
              <Outlet context={outletContext} />
            </ComingSoonOverlay>
          ) : (
            <Outlet context={outletContext} />
          )}
        </div>

        <CityHubAside city={city} recentPhotosRevision={recentPhotosRevision} />
      </div>
    </div>
  )
}

export default CityHubLayout
